import { useState, useEffect, useMemo, useRef } from 'react';
import ReactInputMask from 'react-input-mask';
import useInput from 'hooks/useInput';
import ValidIcon from 'components/common/icons/valid';
import { useRecoilValue, useRecoilState } from 'recoil';
import { inputState, formState, fieldAsyncValidationForField } from 'state';
import { v2 } from 'components/common/form/formValidationRules';
import { getEntryByLocale } from 'api/contentful';
import Dropdown from '../form/dropdowns/ApiDropdown';
import AddressAutocomplete from '../form/dropdowns/AddressAutocomplete';
import { findInDictionary } from 'helpers/parse';
import { getMarketConfigByCountry, getMarketConfigByInputProps } from 'helpers/marketConfig';
import { Checkbox, Link, TextField } from '@newulife/common';
import { FormControlLabel } from '@mui/material';
import { Stack } from '@mui/system';
import localizedData from 'translations/pages/create-account';

// Import the CSS file
import './Input.css';

export function Identity(props) {
  let { labelRef } = props;
  const inputs = useRecoilValue(inputState);
  const { COMPANY } = inputs;

  if (COMPANY) labelRef = 'BUSINESS_IDENTITY';
  const marketProps = getMarketConfigByInputProps(props);
  const newProps = {
    ...marketProps,
    labelRef,
  };

  return <InputType {...newProps} />;
}

function Password(props) {
  const { isValid, className = '' } = props;
  const id = 'PASSWORD';
  const { locale } = useRecoilValue(formState);
  const [secure, setSecure] = useState(true);
  const [showRequirements, setshowRequirements] = useState(false);
  const [text, setText] = useState([]);

  useEffect(() => {
    getEntryByLocale('1KsGscO93HK8GFqnX5EbrA', locale).then((res) => {
      const {
        fields: { title, pdfReferences = [] },
      } = res;
      const singleLines = pdfReferences.map(({ fields: { textString } }) => textString);
      if (props) setText([title, ...singleLines]);
    });
  }, [locale]);

  const { handleChange, value } = useInput(id);

  function toggleShowRequirements() {
    if (!showRequirements) setshowRequirements(true);
    if (validate()) {
      setshowRequirements(false);
    }
  }

  function validate() {
    if (checkLength() && hasLowercase() && hasUppercase() && hasNumber()) return true;
    return false;
  }

  useEffect(() => {
    if (validate()) isValid(true);
    else isValid(false);
  }, [value]);
  function checkLength() {
    return value?.length >= 8;
  }
  function hasUppercase() {
    return value.match('[A-Z]+');
  }
  function hasLowercase() {
    return value.match('[a-z]+');
  }
  function hasNumber() {
    return value.match('[0-9]+');
  }
  const [
    label,
    show,
    hide,
    validateUppercase,
    validateLowercase,
    validateNumber,
    validateCharacters,
  ] = text;

  return (
    <div className="password-wrapper">
      <div className="input-wrapper">
        <input
          type={secure ? 'password' : 'text'}
          id="PASSWORD"
          onChange={(e) => handleChange(e)}
          value={value}
          name="PASSWORD"
          onFocus={(e) => toggleShowRequirements()}
          onBlur={(e) => toggleShowRequirements()}
          className={`${className} ${value ? 'text-input-filled' : ''}`.trim()}
        />
        <label htmlFor="PASSWORD" aria-label="password">
          {label}
        </label>
        <button onClick={(_) => setSecure(!secure)} type="button" id="passwordShow">
          {secure ? show : hide}
        </button>
      </div>

      <ul className={`validation-list ${showRequirements ? 'active' : ''}`}>
        <Validation id="check-length" text={validateCharacters} validationFunction={checkLength} />
        <Validation id="check-upper" text={validateUppercase} validationFunction={hasUppercase} />
        <Validation id="check-lower" text={validateLowercase} validationFunction={hasLowercase} />
        <Validation id="check-number" text={validateNumber} validationFunction={hasNumber} />
      </ul>
    </div>
  );
}

function Validation(props) {
  const { id, text, validationFunction } = props;
  return (
    <li id={id}>
      <ValidIcon isValid={validationFunction()} />
      <p>{text}</p>
    </li>
  );
}

export function BlueSwitch({
  defaultChecked = false,
  id,
  links = [],
  isChecked: setParentIsChecked = () => {},
  label,
  onChange: propsOnChange = () => {},
  hack,
  onOpenModal,
}) {
  const [checked, set_checked] = useState(defaultChecked);

  function handleChange(e) {
    const { checked } = e.target;
    set_checked(checked);
    propsOnChange(checked);
  }

  return (
    <FormControlLabel
      data-testid={`toggle-${id}`}
      sx={{
        alignItems: 'flex-start',
      }}
      control={
        <Checkbox
          className={hack ? 'js-check' : ''}
          id={id}
          name={id}
          aria-label={label}
          onChange={handleChange}
          checked={checked}
        />
      }
      label={
        <Stack gap={1} pt={0.3}>
          {!!label?.length && <label htmlFor={id}>{label}</label>}
          {links?.map(({ text, url }, i) => (
            <Link
              key={`toggle-link-${i}`}
              data-testid="termsAndConditions-pdf"
              target="_blank"
              underline="hover"
              sx={{
                fontWeight: 500,
                display: 'block',
              }}
              href={url}
            >
              {text}
            </Link>
          ))}
        </Stack>
      }
    />
  );
}

export function TextInput(props) {
  const {
    id,
    label,
    isValid: setValidCheckFromProps = () => {}, // optional
    forId: forIdFromProps, // optional
    autoCompleteName = '', // optional
    onChange: propsOnChange = () => {}, // optional
    onBlur: propsOnBlur, // optional
    onBlurArray: propsOnBlurArray, // optional
    value: propsValue = '', // optional
    validationChecks,
    dictionary,
    disabled,
    inputObj = { helperText: '' },
    locale,
    mask,
    required = true,
    hide = false,
    country,
    market,
    forceError,
    dataTestId,
    requiresAsyncValidation = false,
    validateOnMount = false, // optional — run blur/async validation on mount (e.g. Shopify prefilled email)
    className = '', // optional
  } = props;

  const forId = forIdFromProps || id;
  const [errorMessageId, set_errorMessageId] = useState();
  const [validityString, set_validityString] = useState('untouched'); // ['untouched', 'dirty', 'valid']
  const [asyncValidationCompleted, set_asyncValidationCompleted] = useState(false);
  const [fieldValidation, setFieldValidation] = useRecoilState(fieldAsyncValidationForField(id));
  const [isAsyncValidationRunning, setIsAsyncValidationRunning] = useState(false);
  const validateOnMountTriggered = useRef(false);

  const { lastValidatedValue, lastValidationSuccess } = fieldValidation;

  // validates optional fields with no validation checks immediately
  useEffect(() => {
    checkValueValidity(propsValue);
  }, [propsValue]);

  useEffect(() => {
    // For fields requiring async validation, only consider valid if both basic validation passes AND async validation completed
    const isValid = requiresAsyncValidation
      ? validityString === 'valid' && asyncValidationCompleted
      : validityString === 'valid';
    setValidCheckFromProps(isValid);
  }, [validityString, asyncValidationCompleted, requiresAsyncValidation]);

  // Restore form validity from Recoil when remounting (e.g. navigate back): if we already
  // successfully async-validated this value, set validityString and asyncValidationCompleted
  // so setValidCheckFromProps(true) is sent and the Next button enables.
  useEffect(() => {
    if (
      requiresAsyncValidation &&
      lastValidationSuccess === true &&
      propsValue &&
      propsValue === lastValidatedValue
    ) {
      set_validityString('valid');
      set_asyncValidationCompleted(true);
    }
  }, [requiresAsyncValidation, lastValidationSuccess, lastValidatedValue, propsValue]);

  // Shopify (and similar) entry points: prefilled values never receive a blur, so run the same
  // sync + async onBlur validation once on mount.
  useEffect(() => {
    if (!validateOnMount || validateOnMountTriggered.current) return;
    if (propsOnBlurArray || !propsOnBlur || typeof propsOnBlur !== 'object') return;

    validateOnMountTriggered.current = true;

    if (!propsValue) {
      if (required) {
        set_validityString('error');
        set_errorMessageId('requiredFields');
      }
      return;
    }

    v2(validationChecks)(propsValue).then((errorMessage) => {
      if (errorMessage) {
        set_validityString('error');
        set_errorMessageId(errorMessage);
        return;
      }

      set_validityString('valid');
      set_errorMessageId();

      if (!requiresAsyncValidation) return;

      if (propsValue === lastValidatedValue) {
        if (lastValidationSuccess === false) {
          set_validityString('error');
          set_errorMessageId(propsOnBlur[1]);
          set_asyncValidationCompleted(false);
        } else if (lastValidationSuccess === true) {
          set_asyncValidationCompleted(true);
        }
        return;
      }

      const [apiCall, errorMessageId] = propsOnBlur;
      setIsAsyncValidationRunning(true);
      apiCall(propsValue, country, market).then((response) => {
        setFieldValidation({ lastValidatedValue: propsValue, lastValidationSuccess: !!response });
        if (!response) {
          set_validityString('error');
          set_errorMessageId(errorMessageId);
          set_asyncValidationCompleted(false);
        } else {
          set_asyncValidationCompleted(true);
        }
        setIsAsyncValidationRunning(false);
      });
    });
  }, [validateOnMount, propsValue]);

  function handleChange(event) {
    set_validityString('dirty');
    let { value } = event.target;

    if (mask) {
      const placeholder = mask.replace(/[9a*]/g, '_');

      // If value is all placeholder junk, wipe it
      if (value === placeholder) {
        value = undefined;
      } else {
        value = cleanMaskedValue(value, mask);
      }
    }

    // Reset async validation state when value changes
    if (requiresAsyncValidation) {
      set_asyncValidationCompleted(false);
      setFieldValidation({ lastValidatedValue: undefined, lastValidationSuccess: undefined });
    }

    propsOnChange(id, value);
  }

  function cleanMaskedValue(value, mask) {
    if (!mask) return value;

    let cleaned = '';

    for (let i = 0; i < value.length; i++) {
      const maskChar = mask[i];
      const valChar = value[i];

      if (!valChar) continue;

      switch (maskChar) {
        case '9':
          if (/\d/.test(valChar)) cleaned += valChar;
          break;
        case 'a':
          if (/[A-Za-z]/.test(valChar)) cleaned += valChar;
          break;
        case '*':
          if (/[A-Za-z0-9]/.test(valChar)) cleaned += valChar;
          break;
        default:
          // keep literal characters like 'T', '-', etc.
          cleaned += valChar;
      }
    }

    return cleaned;
  }

  const checkValueValidity = (value = '') => {
    const setError = (error = 'requiredFields') => {
      set_validityString('error');
      set_errorMessageId(error);
    };
    const setValid = () => {
      set_validityString('valid');
      set_errorMessageId();
    };
    if (!value && !required) setValid();
    else if (!value && required && validityString === 'untouched') return;
    else if (!value && required) setError();
    else if (value) {
      v2(validationChecks)(value).then((errorMessage) => {
        if (!errorMessage) {
          setValid();
        } else {
          setError(errorMessage);
        }
      });
    } else {
      // shouldn't reach this code
      throw Error('Input validation error', '', 'src/components/common/inputs/Input.jsx', 277);
    }
  };

  function handleBlur(e) {
    let { value } = e.target;

    if (mask) {
      const placeholder = mask.replace(/[9a*]/g, '_');
      if (value === placeholder) {
        value = undefined;
      } else {
        value = cleanMaskedValue(value, mask);
      }
    }

    // NEW LOGIC: Only run checkValueValidity if value changed or not using async validation
    if (requiresAsyncValidation && value === lastValidatedValue) {
      // If last validation was a failure, keep error state
      if (lastValidationSuccess === false) {
        set_validityString('error');
        // Don't clear errorMessageId, keep the last one
      }
      // Don't run checkValueValidity, don't clear error
    } else {
      checkValueValidity(value);
      if (required && !value) {
        set_validityString('error');
        set_errorMessageId('requiredFields');
      }
    }

    if (propsOnBlur && typeof propsOnBlur === 'function' && !propsOnBlurArray) propsOnBlur(e);
    if (
      propsOnBlur &&
      !propsOnBlurArray &&
      typeof propsOnBlur === 'object' &&
      validityString === 'valid' &&
      value
    ) {
      // Only fire API if value has changed since last validation
      if (value !== lastValidatedValue) {
        const [apiCall, errorMessageId] = propsOnBlur;
        if (requiresAsyncValidation) setIsAsyncValidationRunning(true);
        apiCall(value, country, market).then((response) => {
          setFieldValidation({ lastValidatedValue: value, lastValidationSuccess: !!response });
          if (!response) {
            set_validityString('error');
            set_errorMessageId(errorMessageId);
            if (requiresAsyncValidation) {
              set_asyncValidationCompleted(false);
              setIsAsyncValidationRunning(false);
            }
          } else {
            if (requiresAsyncValidation) {
              set_asyncValidationCompleted(true);
              setIsAsyncValidationRunning(false);
            }
          }
        });
      } else {
        // If last validation was a failure, keep error state
        if (lastValidationSuccess === false) {
          set_validityString('error');
        }
      }
    }
    if (validityString === 'valid' && value) {
      propsOnBlurArray?.forEach?.((onBlurFn) => {
        const [apiCall, errorMessageId] = onBlurFn;
        if (errorMessageId === 'CUSTOM') {
          apiCall();
        } else {
          if (requiresAsyncValidation) setIsAsyncValidationRunning(true);
          apiCall(value, country, market).then((response) => {
            if (!response) {
              set_validityString('error');
              if (errorMessageId) set_errorMessageId(errorMessageId);
              if (requiresAsyncValidation) {
                set_asyncValidationCompleted(false);
                setIsAsyncValidationRunning(false);
                setFieldValidation((prev) => ({ ...prev, lastValidationSuccess: false }));
              }
            } else {
              if (requiresAsyncValidation) {
                set_asyncValidationCompleted(true);
                setIsAsyncValidationRunning(false);
                setFieldValidation((prev) => ({ ...prev, lastValidationSuccess: true }));
              }
            }
          });
        }
      });
    }
  }

  const inputMaskProps = {
    mask,
    id,
    name: autoCompleteName,
    autoComplete: autoCompleteName,
    value: propsValue,
    onChange: handleChange,
    onBlur: handleBlur,
    disabled: disabled,
    lang: locale,
    alwaysShowMask: false,
    className: `${className} ${propsValue ? 'text-input-filled' : ''}`.trim(),
  };
  if (hide) return null;
  return (
    <div className={`input-wrapper ${validityString}`}>
      <ReactInputMask {...inputMaskProps} data-testid={dataTestId} />
      <InputLabel id={forId} label={label} />
      {errorMessageId || forceError ? (
        <InputErrorMessage
          dictionary={dictionary}
          id={id}
          messageId={errorMessageId || forceError}
        />
      ) : (
        <p className="helper-text">{inputObj.helperText}</p>
      )}
      {/* Loading indicator for async validation */}
      {requiresAsyncValidation && (isAsyncValidationRunning || lastValidationSuccess) && (
        <div
          className={`async-validation-loader ${lastValidationSuccess ? 'success-checkmark' : ''}`}
        >
          {!lastValidationSuccess && (
            <>
              <div className="loading-line">
                <div className="gradient-bar" />
              </div>
              <span className="validating-text">
                {findInDictionary(dictionary, 'validating_text') || 'Validating...'}
              </span>
            </>
          )}
          {lastValidationSuccess && (
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M1.23242 6.64611L6.14441 11L14.575 1" />
            </svg>
          )}
        </div>
      )}
    </div>
  );
}

export function InputLabel({ label: labelFromProps, id, labelId, dictionary }) {
  const label = labelFromProps || findInDictionary(dictionary, labelId);
  if (!label) return null;
  return (
    <label htmlFor={id} aria-label={id}>
      {label}
    </label>
  );
}
export function InputErrorMessage(props) {
  const { id, messageId, dictionary, styleOverride } = props;
  const style = {
    display: 'inline-flex',
    fontFamily: 'Inter,sans-serif',
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '14px',
    color: '#9a3b26',
    ...styleOverride,
  };

  const message = findInDictionary(dictionary, messageId) || messageId;
  if (!message) return null;
  return (
    <p className="helper-text error" style={style} data-testid={`${id}-required`}>
      {message}
    </p>
  );
}

export default function InputConfig(props) {
  if (!props?.country || !props?.id)
    throw `country-(${props?.country}) and id-(${props?.id}) props are required for inputs`;
  const marketConfig = getMarketConfigByCountry(props.country).inputs[props.id];
  return <InputSelector {...props} {...marketConfig} />;
}
// Selects the Input Component based on id so properties can be added or changed by country or other requirements

function InputSelector(props) {
  const { id } = props;
  switch (id) {
    case 'IDENTITY':
      return <Identity {...props} />;
    case 'PASSWORD':
      return <Password {...props} />;
    default:
      return <DataLink {...props} />;
  }
}

function DataLink(props) {
  const inputs = useRecoilValue(inputState);
  let { formType = 'account' } = props;
  const marketProps = getMarketConfigByInputProps(props);
  const { dataLink } = marketProps;

  /* Memoize so we don't create new object refs when only unrelated form fields change.
    getMarketConfigByCountry returns new array refs each render - use stable keys/values for deps
    instead of dataLink/inputs to avoid rebuild on every keystroke in unrelated fields. 
*/
  const dataLinkKeys = Array.isArray(dataLink) ? dataLink : [dataLink];
  const linkedValues = dataLinkKeys.map((key) => inputs[`${formType.toUpperCase()}_${key}`]);
  const linkedValuesKey = JSON.stringify(linkedValues);

  const apiCallData = useMemo(() => {
    if (Array.isArray(dataLink)) {
      return dataLink.reduce((acc, item) => {
        acc[item] = inputs[`${formType.toUpperCase()}_${item}`];
        return acc;
      }, {});
    }
    return inputs[`${formType.toUpperCase()}_${dataLink}`];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dataLink/inputs refs change every render; we depend on linkedValuesKey (values) and formType only
  }, [formType, dataLinkKeys.join(','), linkedValuesKey]);

  const newProps = {
    ...marketProps,
    apiCallData,
  };
  return <InputType {...newProps} />;
}

function InputType(props) {
  const {
    dictionary,
    formType = 'account',
    type = 'text',
    labelRef,
    id: propsId,
    soId,
    stateRef,
    field,
    value: propsValue,
    onChange: propsOnChange = () => {},
    storeInMiscFields = false,
    ...restOfProps
  } = props;

  const label = findInDictionary(dictionary, labelRef) || findInDictionary(dictionary, propsId);

  const [inputs, set_inputs] = useRecoilState(inputState);

  const fieldId = stateRef || propsId;
  const value =
    propsValue || (storeInMiscFields ? inputs.miscFields?.[fieldId] || '' : inputs[fieldId]);

  function onChange_AddToStore(id, value) {
    if (storeInMiscFields) {
      set_inputs((prev) => {
        const { [id]: removed, ...restInputs } = prev;
        return {
          ...restInputs,
          miscFields: {
            ...prev.miscFields,
            [id]: value,
          },
        };
      });
    } else {
      set_inputs({ ...inputs, [id]: value });
    }
    propsOnChange(id, value);
  }

  const newProps = {
    ...restOfProps,
    label,
    id: stateRef || propsId,
    formType,
    dictionary,
    type,
    value,
    onChange: onChange_AddToStore,
    field,
    stateRef,
  };

  switch (type) {
    case 'switch':
      return <BinarySelector {...newProps} />;
    case 'dropdown':
      return <Dropdown {...newProps} />;
    case 'google-autocomplete':
      return <AddressAutocomplete {...newProps} />;
    default:
      return <TextInput {...newProps} />;
  }
}

function BinarySelector(props) {
  const { locale } = useRecoilValue(formState);
  const { isValid, onChange: propsOnChange, id, data: { list = [] } = {}, ...rest } = props;

  const urls = localizedData(props.country, locale);
  const switchConfig = {
    TERMS: {
      links: [{ text: list[0], url: urls?.termsPDF }],
    },
    GAIYOSHOMEN: {
      links: [{ text: list[1], url: urls?.gaiyoshomenPDF }],
    },
    KR_PRIVACY_POLICY: {
      links: [{ text: list[2], url: urls?.privacyPDF }],
    },
    TH_PDPA: {
      links: [{ text: list[3], url: urls?.pdpaPDF }],
    },
  };
  function onChange(value) {
    isValid(value);
    propsOnChange(id, value);
  }
  const newProps = {
    ...rest,
    id,
    onChange,
    links: switchConfig?.[id]?.links,
  };
  return <BlueSwitch {...newProps} />;
}
